/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable unused-imports/no-unused-vars */
import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import {
  Box,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Text as TextIcon,
  Image as ImageIcon,
  Table as TableIcon,
  Blocks as SignatureIcon,
  QrCode as QrCodeIcon,
  Barcode as BarcodeIcon,
  LayoutGrid,
} from 'lucide-react';
import { styled } from '@mui/material';

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1.5),
  cursor: 'grab',
  borderRadius: theme.shape.borderRadius,
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
  '&.dragging': {
    opacity: 0.5,
  },
}));

const SidebarItem = ({ id, icon, label }: { id: string; icon?: React.ReactElement; label: string }) => {
  // Extract the element type from the sidebar item id
  const elementType = typeof id === 'string' ? id.replace('sidebar-', '') : id;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: {
      type: elementType,
      label,
    },
  });

  return (
    <StyledListItem
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={isDragging ? 'dragging' : ''}
      sx={{ border: '1px solid', borderColor: 'grey.200', p: 0.75 }}
    >
      <ListItemIcon sx={{ minWidth: 32 }}>{icon}</ListItemIcon>
      <ListItemText 
        primary={label} 
        primaryTypographyProps={{ 
          variant: 'body2', 
          sx: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } 
        }} 
      />
    </StyledListItem>
  );
};

interface EditorSidebarProps {
  onHtmlUpload?: (html: string) => void;
}

const EditorSidebar = ({ onHtmlUpload }: EditorSidebarProps) => {
  const sections = [
    {
      id: 'text',
      icon: <TextIcon size={18} />,
      label: 'Text',
    },
    {
      id: 'image',
      icon: <ImageIcon size={18} />,
      label: 'Image',
    },
    {
      id: 'buttons',
      icon: (
        <Box
          sx={{
            width: 16,
            height: 16,
            bgcolor: 'grey.400',
            borderRadius: 1,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '8px',
          }}
        >
          BTN
        </Box>
      ),
      label: 'Buttons',
    },
    {
      id: 'divider',
      icon: <Divider sx={{ width: 18 }} />,
      label: 'Divider',
    },
    {
      id: 'table',
      icon: <TableIcon size={18} />,
      label: 'Table',
    },
    {
      id: 'signature',
      icon: <SignatureIcon size={18} />,
      label: 'Signature',
    },
    {
      id: 'qr-code',
      icon: <QrCodeIcon size={18} />,
      label: 'QR Code',
    },
    {
      id: 'bar-code',
      icon: <BarcodeIcon size={18} />,
      label: 'Bar Code',
    },
  ];

  return (
    <Box
      className="te-sidebar"
      sx={{
        bgcolor: 'background.paper',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
      }}
    >
      <Box sx={{ flexGrow: 1, p: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mt: 1 }}>
          {sections.map((section) => (
            <SidebarItem key={section.id} id={`sidebar-${section.id}`} icon={section.icon} label={section.label} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export { EditorSidebar };
export default EditorSidebar;